import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const VERDICT_COLOR = {
  PROCEED: 'bg-green-100 text-green-700',
  HOLD: 'bg-yellow-100 text-yellow-700',
  REDUCE: 'bg-orange-100 text-orange-700',
  SKIP: 'bg-red-100 text-red-700',
  UNREVIEWED: 'bg-gray-100 text-gray-500',
};
const verdictColor = (v) => VERDICT_COLOR[(v || '').toUpperCase()] || 'bg-gray-100 text-gray-500';

const fmtMonth = (m) => {
  if (!m) return '';
  const d = new Date(`${m}T00:00:00`);
  return isNaN(d) ? m : d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};
const pct = (v) => (v == null || isNaN(Number(v)) ? '—' : `${(Number(v) * 100).toFixed(1)}%`);
const num = (v, d = 2) => (v == null || isNaN(Number(v)) ? '—' : Number(v).toFixed(d));

const PAGE_SIZES = [10, 25, 50, 100];

const AiRank = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [verdictFilter, setVerdictFilter] = useState('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0); // zero-based
  const [expanded, setExpanded] = useState(() => new Set());

  // Fundamental filters. '' = no filter (any); for the tri-state selects, 'yes'/'no' map to booleans.
  const [minCapInput, setMinCapInput] = useState('');
  const [maxCapInput, setMaxCapInput] = useState('');
  const [minCap, setMinCap] = useState('');
  const [maxCap, setMaxCap] = useState('');
  const [dividend, setDividend] = useState('');          // '', 'yes', 'no'
  const [epsGrowth, setEpsGrowth] = useState('');         // '', 'yes', 'no'
  const [revGrowth, setRevGrowth] = useState('');         // '', 'yes', 'no'

  // Debounce the market-cap number inputs -> applied values (refetch + reset to page 0).
  useEffect(() => {
    const t = setTimeout(() => {
      setMinCap(minCapInput.trim());
      setMaxCap(maxCapInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [minCapInput, maxCapInput]);

  const triBool = (v) => (v === 'yes' ? true : v === 'no' ? false : undefined);

  // Debounce the search box -> search (which triggers a refetch + resets to page 0).
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get('/strategy/ai-rank', {
        params: {
          verdict: verdictFilter,
          search: search || undefined,
          minCap: minCap !== '' && !isNaN(Number(minCap)) ? Number(minCap) : undefined,
          maxCap: maxCap !== '' && !isNaN(Number(maxCap)) ? Number(maxCap) : undefined,
          dividend: triBool(dividend),
          epsGrowthPositive: triBool(epsGrowth),
          revGrowthPositive: triBool(revGrowth),
          limit: pageSize,
          offset: page * pageSize,
        },
      })
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e.response?.data?.error || 'Could not load AI ranking.'))
      .finally(() => setLoading(false));
  }, [verdictFilter, search, minCap, maxCap, dividend, epsGrowth, revGrowth, pageSize, page]);

  useEffect(() => { load(); }, [load]);

  const rows = (data && Array.isArray(data.rows)) ? data.rows : [];
  const total = (data && data.total) || 0;
  const counts = (data && data.verdictCounts) || {};
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const toggleExpand = (key) =>
    setExpanded((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const anyFundFilter = minCap !== '' || maxCap !== '' || dividend !== '' || epsGrowth !== '' || revGrowth !== '';
  const hasData = data && data.month && (total > 0 || search || verdictFilter !== 'ALL' || anyFundFilter);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">AI Stock Rankings</h1>
          <p className="text-gray-600 mt-2">
            Our AI analyst scores a universe of quality stocks each month and ranks them by
            conviction (Proceed → Hold → Reduce → Skip), then confidence.
          </p>
          {data && data.month && (
            <p className="text-sm text-gray-500 mt-1">Rankings as of {fmtMonth(data.month)}</p>
          )}
        </div>

        {/* Controls: search + verdict chips + page size */}
        {(data && data.month) && (
          <div className="bg-white rounded-lg shadow-sm p-3 mb-3 flex flex-wrap items-center gap-2">
            <input
              type="text"
              placeholder="Search symbol or company…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="flex-1 min-w-[200px] px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            />
            {['ALL', 'PROCEED', 'HOLD', 'REDUCE', 'SKIP'].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => { setVerdictFilter(v); setPage(0); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                  verdictFilter === v ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                }`}
              >
                {v === 'ALL' ? 'All' : v.charAt(0) + v.slice(1).toLowerCase()}
                {v !== 'ALL' && counts[v] != null ? ` (${counts[v]})` : ''}
              </button>
            ))}
            <label className="text-sm text-gray-600 ml-auto">
              Per page:&nbsp;
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm bg-white"
              >
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
        )}

        {/* Fundamental filters: market cap range, dividend, EPS growth, revenue growth */}
        {(data && data.month) && (
          <div className="bg-white rounded-lg shadow-sm p-3 mb-3 flex flex-wrap items-end gap-x-4 gap-y-2">
            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-0.5">Market cap ($B)</label>
              <div className="flex items-center gap-1">
                <input
                  type="number" min="0" step="any" placeholder="Min"
                  value={minCapInput}
                  onChange={(e) => setMinCapInput(e.target.value)}
                  className="w-24 px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                />
                <span className="text-gray-400">–</span>
                <input
                  type="number" min="0" step="any" placeholder="Max"
                  value={maxCapInput}
                  onChange={(e) => setMaxCapInput(e.target.value)}
                  className="w-24 px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>

            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-0.5">Dividend</label>
              <select
                value={dividend}
                onChange={(e) => { setDividend(e.target.value); setPage(0); }}
                className="px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white"
              >
                <option value="">Any</option>
                <option value="yes">Pays dividend</option>
                <option value="no">No dividend</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-0.5">EPS growth (YoY)</label>
              <select
                value={epsGrowth}
                onChange={(e) => { setEpsGrowth(e.target.value); setPage(0); }}
                className="px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white"
              >
                <option value="">Any</option>
                <option value="yes">Positive</option>
                <option value="no">Not positive</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-0.5">Revenue growth (YoY)</label>
              <select
                value={revGrowth}
                onChange={(e) => { setRevGrowth(e.target.value); setPage(0); }}
                className="px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white"
              >
                <option value="">Any</option>
                <option value="yes">Positive</option>
                <option value="no">Not positive</option>
              </select>
            </div>

            {anyFundFilter && (
              <button
                type="button"
                onClick={() => {
                  setMinCapInput(''); setMaxCapInput('');
                  setMinCap(''); setMaxCap('');
                  setDividend(''); setEpsGrowth(''); setRevGrowth('');
                  setPage(0);
                }}
                className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 text-gray-600 hover:border-gray-400"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-10 text-center text-gray-500">Loading rankings…</div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm p-10 text-center text-gray-500">{error}</div>
        ) : !hasData ? (
          <div className="bg-white rounded-lg shadow-sm p-10 text-center">
            <p className="text-gray-800 font-medium mb-1">Rankings refresh monthly.</p>
            <p className="text-sm text-gray-500">The next AI ranking will appear here after the monthly run.</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-3 py-2 w-10">#</th>
                    <th className="px-3 py-2">Symbol</th>
                    <th className="px-3 py-2">Company</th>
                    <th className="px-3 py-2">Verdict</th>
                    <th className="px-3 py-2">Conf.</th>
                    <th className="px-3 py-2">Mkt Cap</th>
                    <th className="px-3 py-2">P/E</th>
                    <th className="px-3 py-2">EPS</th>
                    <th className="px-3 py-2">Rev Grwth</th>
                    <th className="px-3 py-2">EPS Grwth</th>
                    <th className="px-3 py-2">Div</th>
                    <th className="px-3 py-2">Rationale</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const key = `${r.symbol}-${i}`;
                    const isOpen = expanded.has(key);
                    const hasMore = r.rationale && (r.rationale.length > 120 || r.risk_notes);
                    return (
                      <React.Fragment key={key}>
                      <tr className="border-t border-gray-100 align-top hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-400">{page * pageSize + i + 1}</td>
                        <td className="px-3 py-2 font-semibold text-gray-900 whitespace-nowrap">
                          <Link to={`/stock/stock-analyze/${encodeURIComponent(r.symbol)}`} className="text-blue-600 hover:underline">
                            {r.symbol}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-gray-700 max-w-[160px] truncate" title={r.company_name || ''}>{r.company_name || '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${verdictColor(r.verdict)}`}>
                            {r.verdict || 'UNREVIEWED'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{r.confidence != null ? `${Number(r.confidence).toFixed(0)}%` : '—'}</td>
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{r.marketcap_b != null ? `$${r.marketcap_b}B` : '—'}</td>
                        <td className="px-3 py-2 text-gray-500">{num(r.pe, 1)}</td>
                        <td className="px-3 py-2 text-gray-500">{num(r.eps, 2)}</td>
                        <td className={`px-3 py-2 ${Number(r.rev_growth) < 0 ? 'text-red-600' : 'text-gray-500'}`}>{pct(r.rev_growth)}</td>
                        <td className={`px-3 py-2 ${Number(r.eps_growth) < 0 ? 'text-red-600' : 'text-gray-500'}`}>{pct(r.eps_growth)}</td>
                        <td className="px-3 py-2 text-gray-500">{r.dividend != null && Number(r.dividend) > 0 ? `$${num(r.dividend, 2)}` : '—'}</td>
                        <td className="px-3 py-2 text-gray-600 max-w-md">
                          {r.rationale ? (
                            <>
                              {/* Collapsed preview only; the full text expands full-width below. */}
                              {!isOpen && <span className="line-clamp-2">{r.rationale}</span>}
                              {hasMore && (
                                <button
                                  type="button"
                                  onClick={() => toggleExpand(key)}
                                  className="text-blue-600 hover:underline text-xs whitespace-nowrap"
                                >
                                  {isOpen ? 'Show less' : 'Show more'}
                                </button>
                              )}
                            </>
                          ) : '—'}
                        </td>
                      </tr>
                      {isOpen && r.rationale && (
                        <tr className="bg-blue-50/40">
                          <td colSpan={12} className="px-4 py-3">
                            <div className="max-w-5xl">
                              <p className="text-sm text-gray-700 leading-relaxed">{r.rationale}</p>
                              {r.risk_notes && (
                                <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                                  <span className="font-semibold text-gray-700">Risk:</span> {r.risk_notes}
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr><td colSpan={12} className="px-3 py-8 text-center text-gray-500">No stocks match your filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
              <span>
                Showing {total === 0 ? 0 : page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button" disabled={page <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
                >Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button
                  type="button" disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
                >Next</button>
              </div>
            </div>

            <p className="text-xs text-gray-400 mt-6">
              AI-generated, advisory only — not investment advice. Rankings are a point-in-time
              model assessment refreshed monthly and are not backtested predictions of return.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AiRank;
